import { formatINR, getCashbackAmount, getRewardCashback } from "../components/web/VoucherCheckoutPanel";

export const num = (v) => Number(v) || 0;
export const round2 = (n) => Math.round(n * 100) / 100;
const MIN_STEP = 0.01; // highest bill still inside a slab (slab "to" is exclusive)

/* ── slabs ── */
export const parseSlabs = (raw) => {
    let slabs = raw;
    if (typeof slabs === "string") { try { slabs = JSON.parse(slabs); } catch { slabs = null; } }
    return Array.isArray(slabs) ? slabs : [];
};

// (from, to]  → "0–500" includes 500; the next slab starts just above it
export const getSlabForBill = (slabs, bill) =>
    slabs.find((s) => {
        const from = num(s.from);
        const aboveFrom = from === 0 ? bill >= 0 : bill > from; // first slab (from 0) stays inclusive
        return aboveFrom && (s.to == null || bill <= num(s.to));
    }) || null;

export const getHighestSlabMax = (slabs) =>
    slabs.reduce((m, s) => Math.max(m, num(s.max_discount)), 0);

const slabsVary = (slabs) => new Set(slabs.map((s) => num(s.max_discount))).size > 1;

/* ── customer discount model ──
 * reward.discount_enabled: 0 = GoerOne, 1 = Store, 2 = Both, null = no discount (cashback only).
 *  - GoerOne discount is paid out of the merchant commission (dealvalue):
 *      goerone_discount_type 0 → custvalue is the customer's % share of the commission
 *      goerone_discount_type 1 → custvalue is a flat ₹ amount
 *    maxdiscount_slabs cap it (max_discount = most ₹ the discount can ever be for that bill slab).
 *  - Store discount is on the total bill:
 *      store_disc_type 0 → store_disc_value % of the bill, 1 → flat ₹
 *    store_maxdiscount_slabs cap it the same way (absolute ₹).
 * The customer only ever sees the combined discount, never which side gives it.
 */
const partsEnabled = (r) => {
    const v = r?.discount_enabled;
    if (v == null || v === "") return { goerone: false, store: false };
    const n = Number(v);
    return { goerone: n === 0 || n === 2, store: n === 1 || n === 2 };
};

/** Discount components this reward really has (switched on, has a value and at least one slab) */
const activeParts = (r) => {
    const on = partsEnabled(r);
    const parts = [];
    const gSlabs = parseSlabs(r?.maxdiscount_slabs);
    if (on.goerone && num(r.custvalue) > 0 && gSlabs.length) parts.push({ source: "goerone", slabs: gSlabs });
    const sSlabs = parseSlabs(r?.store_maxdiscount_slabs);
    if (on.store && num(r.store_disc_value) > 0 && sSlabs.length) parts.push({ source: "store", slabs: sSlabs });
    return parts;
};

/** Rate of one component for a slab whose max discount is `slabMax`: { fixed, pct, amount, cap } */
const partRate = (r, source, slabMax) => {
    if (source === "store") {
        const fixed = Number(r.store_disc_type) === 1;
        const value = num(r.store_disc_value);
        return { fixed, pct: fixed ? 0 : value, amount: fixed ? value : 0, cap: round2(slabMax) };
    }
    const share = num(r.custvalue);
    if (Number(r.goerone_discount_type) === 1) {
        return { fixed: true, pct: 0, amount: share, cap: round2(slabMax) };
    }
    // % of the merchant commission; the commission itself is a % of the bill or a flat amount
    const own = round2((num(r.dealvalue) * share) / 100);
    const fixed = Number(r.dealtype) === 1;
    return { fixed, pct: fixed ? 0 : own, amount: fixed ? own : 0, cap: round2(slabMax) };
};

export const hasDiscount = (r) => activeParts(r).length > 0;

/** A reward counts once it gives a discount or a cashback */
export const hasBenefit = (r) => hasDiscount(r) || num(r?.rewardvalue) > 0;
export const hasSlabs = hasBenefit; // old name, still imported by StoreDetailPage

/** Bill at which the combined discount of the slab starting at `start` is fully reached (% deals grow until they hit the cap) */
const slabTargetBill = (reward, parts, start) => {
    const reached = parts.map((p) => {
        const slab = getSlabForBill(p.slabs, start);
        const rate = partRate(reward, p.source, num(slab?.max_discount));
        if (rate.fixed || rate.pct <= 0 || rate.cap <= 0) return start;
        const reach = round2((rate.cap * 100) / rate.pct);
        const end = slab?.to == null ? Infinity : round2(num(slab.to)); // highest bill still inside the slab
        return round2(Math.max(start, Math.min(reach, end)));
    });
    return Math.max(start, ...reached);
};

export const isRewardValid = (r, now = new Date()) => {
    if (!hasBenefit(r)) return false;
    if (r.is_active != null && Number(r.is_active) !== 1) return false;
    if (r.stock_quantity != null && num(r.stock_quantity) <= 0) return false;
    if (r.start_date && now < new Date(r.start_date)) return false;
    if (r.end_date) {
        const end = new Date(r.end_date);
        if (String(r.end_date).length <= 10) end.setHours(23, 59, 59, 999);
        if (now > end) return false;
    }
    return true;
};

export function computeRewardDiscount(reward, bill) {
    const parts = activeParts(reward).map((p) => {
        const slab = getSlabForBill(p.slabs, bill);
        const rate = partRate(reward, p.source, num(slab?.max_discount));
        const raw = rate.fixed ? rate.amount : round2((bill * rate.pct) / 100);
        const capApplied = rate.cap > 0 && raw > rate.cap;
        return { ...rate, capApplied, discount: round2(capApplied ? rate.cap : raw) };
    });

    const discount = round2(Math.min(parts.reduce((t, p) => t + p.discount, 0), bill));
    const pctParts = parts.filter((p) => !p.fixed);
    const allPctCapped = pctParts.every((p) => p.cap > 0);
    const anyCapped = parts.some((p) => p.capApplied);
    // A single "X%" label only tells the truth when every % part is capped (or none is) - otherwise show the ₹ amount
    const asAmount = parts.some((p) => p.fixed) || (anyCapped && !allPctCapped);

    return {
        isFixed: asAmount,
        pct: asAmount ? 0 : round2(pctParts.reduce((t, p) => t + p.pct, 0)),
        cap: !asAmount && allPctCapped ? round2(pctParts.reduce((t, p) => t + p.cap, 0)) : 0,
        capApplied: !asAmount && anyCapped,
        discount,
        fixedAmount: asAmount ? discount : 0,
    };
}

export function getBestReward(rewards, bill) {
    return (
        rewards
            .filter((r) => isRewardValid(r) && bill >= num(r.ordervalue))
            .map((r) => ({
                reward: r,
                ...computeRewardDiscount(r, bill),
                cashback: getCashbackAmount(getRewardCashback(r), bill),
            }))
            .filter((x) => x.discount > 0 || x.cashback > 0)
            .sort(
                (a, b) =>
                    b.discount - a.discount ||
                    b.cashback - a.cashback ||
                    num(b.reward.ordervalue) - num(a.reward.ordervalue)
            )[0] || null
    );
}

export function getNextReward(rewards, bill, currentDiscount, currentReward = null) {
    // 1) A higher slab (of either discount) of the reward the customer is already getting
    if (currentReward) {
        const parts = activeParts(currentReward);
        const starts = [...new Set(parts.flatMap((p) => p.slabs.map((sl) => round2(num(sl.from)+ MIN_STEP))))].filter((f) => f > bill);
        const fromSlab = starts
            .map((start) => {
                const unlockBill = slabTargetBill(currentReward, parts, start);
                return {
                    reward: currentReward,
                    kind: "slab",
                    unlockBill,
                    need: round2(unlockBill - bill),
                    ...computeRewardDiscount(currentReward, unlockBill),
                };
            })
            .filter((x) => x.unlockBill > bill && x.discount > currentDiscount)
            .sort((a, b) => a.unlockBill - b.unlockBill)[0];
        if (fromSlab) return fromSlab;
    }

    // 2) No better slab → next reward that isn't unlocked yet, if it's better
    return (
        rewards
            .filter((r) => isRewardValid(r) && bill < num(r.ordervalue))
            .map((r) => {
                const unlockBill = round2(num(r.ordervalue));
                return {
                    reward: r,
                    kind: "reward",
                    unlockBill,
                    need: round2(unlockBill - bill),
                    ...computeRewardDiscount(r, unlockBill),
                };
            })
            .filter((x) => x.discount > currentDiscount)
            .sort((a, b) => a.unlockBill - b.unlockBill)[0] || null
    );
}

/** Slab unlock → exact ₹ they will get; reward unlock → that reward's own label */
export const nextRewardLabel = (x) => (x.kind === "slab" ? formatINR(x.discount) : rewardLabel(x));

export const rewardLabel = (x) => (x.isFixed ? formatINR(x.fixedAmount) : `${x.pct}%`);

/** Bill-independent headline of a reward, for cards and the Pay Bill button (GoerOne + Store combined) */
export function getRewardHeadline(r) {
    const parts = activeParts(r).map((p) => {
        const rate = partRate(r, p.source, getHighestSlabMax(p.slabs));
        const amount = rate.fixed ? (rate.cap > 0 ? Math.min(rate.amount, rate.cap) : rate.amount) : 0;
        return { ...rate, amount, vary: slabsVary(p.slabs) };
    });
    const pctParts = parts.filter((p) => !p.fixed);
    const isFixed = parts.length > 0 && pctParts.length === 0;
    const pct = round2(pctParts.reduce((t, p) => t + p.pct, 0));
    const amount = round2(parts.reduce((t, p) => t + p.amount, 0));
    const cap = pctParts.length && pctParts.every((p) => p.cap > 0) ? round2(pctParts.reduce((t, p) => t + p.cap, 0)) : 0;
    const vary = parts.some((p) => p.vary);

    let text = "";
    if (parts.length) {
        if (isFixed) text = `${vary ? "Up to " : ""}${formatINR(amount)} OFF`;
        else if (amount <= 0) text = `${pct}% OFF`;
        else text = cap > 0 ? `Up to ${formatINR(round2(amount + cap))} OFF` : `${pct}% + ${formatINR(amount)} OFF`;
    }
    return {
        isFixed,
        pct,
        amount,
        cap,
        text,
        // biggest ₹ it can ever give (uncapped % = unlimited)
        potential: isFixed ? amount : cap > 0 ? round2(amount + cap) : pctParts.length ? Infinity : 0,
    };
}

/** Reward to advertise on the Pay Bill button */
export function getBestHeadline(rewards) {
    return (
        rewards
            .filter(isRewardValid)
            .map(getRewardHeadline)
            .filter((h) => (h.isFixed ? h.amount : h.pct) > 0)
            .sort((a, b) => b.potential - a.potential || b.pct - a.pct)[0] || null
    );
}
/**
 * Nearby / discover LIST discount summary.
 *
 * Those list APIs return one flattened, camelCase `discount` object per store instead of the
 * `rewards` array used on the store-detail page. It is mapped onto the reward shape and run through
 * the same helpers as the detail page (GoerOne + Store combined), so list cards and detail page agree.
 * Returns null when the reward gives neither a discount nor a cashback.
 */
export function getListDiscountSummary(discount) {
    if (!discount) return null;

    const reward = {
        dealtype: discount.dealType,
        dealvalue: discount.dealValue,
        custvalue: discount.custvalue,
        maxdiscount_slabs: discount.maxdiscount_slabs,
        goerone_discount_type: discount.goeroneDiscountType,
        discount_enabled: discount.discountEnabled,
        store_disc_type: discount.storeDiscType,
        store_disc_value: discount.storeDiscValue,
        store_maxdiscount_slabs: discount.store_maxdiscount_slabs,
        rewardtype: discount.rewardType,
        rewardvalue: discount.rewardValue,
        max_reward_value: discount.max_reward_value,
    };

    const cashback = getRewardCashback(reward);
    if (!hasDiscount(reward) && !cashback) return null;

    const h = getRewardHeadline(reward);
    const discountText = h.text ? h.text.replace(" OFF", " off") : null; // null for a cashback-only reward
    const dealAmount = h.amount;
    const dealPct = h.pct;

    let cashbackText = null;
    let cashAmount = 0;
    let cashPct = 0;
    if (cashback) {
        const isCashFixed = cashback.type === "fixed";
        cashPct = isCashFixed ? 0 : cashback.value;
        cashAmount = isCashFixed ? (cashback.max > 0 ? Math.min(cashback.value, cashback.max) : cashback.value) : 0;
        cashbackText = isCashFixed ? `${formatINR(cashAmount)} cashback` : `${cashPct}% cashback`;
    }

    const minOrderValue = num(discount.minOrderValue);

    return { discountText, cashbackText, minOrderValue, dealAmount, dealPct, cashAmount, cashPct };
}

/**
 * Member with offers at this store -> only their membership's offers.
 * No membership (or none of theirs has offers here) -> all offers.
 */
export function getVisibleRewards(rewards, ownedProgramIds = []) {
    rewards = rewards.filter(hasBenefit);
    const owned = new Set(ownedProgramIds.map(Number));
    if (owned.size === 0) return { visibleRewards: rewards, isMember: false };

    const mine = rewards.filter((r) => owned.has(Number(r.program_id)));
    return mine.length
        ? { visibleRewards: mine, isMember: true }
        : { visibleRewards: rewards, isMember: false };
}